import { map, switchMap, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { TicketItem, TicketSalesforce, TicketStatus } from '@shared/models/Ticket';
import { HttpHandlerService } from '@shared/services/http-handler.service';
import { GenericResponse } from '@shared/models/GenericResponse';
import { factory } from '@shared/operators/factory.operator';
import { DocUtils } from '@shared/utils/docs.utils';
import { environment } from '@env';
import { User } from '@shared/models/User';
import { FeatureFlagService } from '@shared/services/feature-flag.service';

const BASE_URL = `${environment.ticketsUrl}/api/v1/chamados`;

@Injectable({ providedIn: 'root' })
export class TicketService {

    constructor(
        private http: HttpHandlerService,
        private ff: FeatureFlagService
    ) { }

    public fetch(filter: any) {
        if (filter.cnpjEmpresa) {
            filter.cnpjEmpresa = DocUtils.cleanMask(filter.cnpjEmpresa);
        }
        return this.http.request<GenericResponse<TicketSalesforce>>(BASE_URL)
            .setParams(filter)
            .setErrorMessage('Falha ao encontrar chamados')
            .get()
            .pipe(
                factory(TicketSalesforce),
                map(results => {
                    results.records = results.records.map(ticket => {
                        ticket.Status_Atual__c = ticket.Status_Atual__c?.replace(/_/g, ' ') as TicketStatus;
                        return ticket;
                    });
                    return results;
                })
            );
    }

    public getById(id: string) {
        return this.fetch({})
            .pipe(map(response => {
                response.record = response.records.find(ticket => ticket.Id.includes(id));
                delete response.records;
                return response;
            }));
    }

    public create(Contabilidade__c: string, Empresa__c: string, Texto_Abertura__c: string, Produto__c: string, Tipo_Atendimento__c: string, linkArquivo?: string) {
        const user = User.fromLocalStorage();
        const body = {
            Contabilidade__c,
            Empresa__c,
            Texto_Abertura__c,
            Produto__c,
            Tipo_Atendimento__c,
            Status_Atual__c: TicketStatus.AGUARDANDO_ANALISE,
            Nome_Cliente__c: `${user.firstName} ${user.lastName || ''}`.trim()
        };
        const request = this.http.request<{ message: string; }>(BASE_URL)
            .setLoadingMessage('Abrindo chamado')
            .setErrorMessage('Falha ao abrir chamado!')
            .setBody(body)
            .post();
        if (linkArquivo) {
            return request.pipe(switchMap(result => {
                return this.appendFile({ Id: result.message } as any, linkArquivo);
            }));
        }
        return request;
    }

    public appendText(to: TicketSalesforce, text: string, link?: string) {
        const url = `${BASE_URL}/movimento`;
        const body = { Atendimento__c: to.Id, Origem__c: 'Cliente', Descricao__c: text, Responsavel__c: to.Responsavel_Processo_Atendimento__c };
        if (link) {
            if (!link.endsWith('/download/tareffa')) {
                link = `${link}/download/tareffa`;
            }
            Object.assign(body, { Link_Arquivo: link });
        }
        return this.http.request(url)
            .setLoadingMessage('Criando movimento')
            .setErrorMessage('Falha ao criar movimento')
            .setBody(body)
            .post();
    }

    public appendFile(to: TicketSalesforce, link: string) {
        return this.appendText(to, '', link);
    }

    public getItems(of: TicketSalesforce) {
        const url = `${BASE_URL}/${of.Id}`;
        return this.http.request<GenericResponse<TicketItem>>(url)
            .setLoadingMessage('Buscando movimentos')
            .setErrorMessage('Falha ao buscar movimentos')
            .get();
    }

}
